// src/api/integrationsApi.ts

import { baseApi } from './baseApi';
import type {
  IntegrationConnection,
  CreateIntegrationRequest,
  UpdateIntegrationRequest,
  TestIntegrationResponse,
  GetIntegrationStatusResponse,
  TestIntegrationByProviderRequest,
  TestIntegrationByProviderResponse,
  SetupIntegrationRequest,
  SetupIntegrationResponse,
} from '../types/api';

export const integrationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ============================================================
    // Integration Wizard Endpoints
    // ============================================================

    /**
     * Get status of all wizard-supported providers for the current workspace.
     */
    getIntegrationStatus: builder.query<GetIntegrationStatusResponse, void>({
      query: () => '/integrations/status',
      providesTags: [{ type: 'Integration', id: 'STATUS' }],
    }),

    /**
     * Test an integration connection (new or existing) without persisting.
     */
    testIntegrationByProvider: builder.mutation<TestIntegrationByProviderResponse, TestIntegrationByProviderRequest>({
      query: (body) => ({
        url: '/integrations/test',
        method: 'POST',
        body,
      }),
    }),

    /**
     * Create or update an integration (upsert).
     */
    setupIntegration: builder.mutation<SetupIntegrationResponse, SetupIntegrationRequest>({
      query: (body) => ({
        url: '/integrations/setup',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Integration', id: 'LIST' },
        { type: 'Integration', id: 'STATUS' },
      ],
    }),

    // ============================================================
    // List Integrations
    // ============================================================
    listIntegrations: builder.query<IntegrationConnection[], void>({
      query: () => '/integrations',
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Integration' as const, id })),
              { type: 'Integration', id: 'LIST' },
            ]
          : [{ type: 'Integration', id: 'LIST' }],
    }),

    // ============================================================
    // Get Single Integration
    // ============================================================
    getIntegration: builder.query<IntegrationConnection, string>({
      query: (id) => `/integrations/${id}`,
      providesTags: (result, error, id) => [{ type: 'Integration', id }],
    }),

    // ============================================================
    // Create Integration
    // ============================================================
    // INVALIDATIONS: every mutation that changes integration state
    // (create / update / delete / test) MUST invalidate BOTH the
    // 'LIST' tag (read by useListIntegrationsQuery on /settings) AND
    // the 'STATUS' tag (read by useGetIntegrationStatusQuery in the
    // setup wizard). Otherwise the wizard will keep showing a stale
    // "Connected" badge after the user disconnects.
    createIntegration: builder.mutation<IntegrationConnection, CreateIntegrationRequest>({
      query: (body) => ({
        url: '/integrations',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Integration', id: 'LIST' },
        { type: 'Integration', id: 'STATUS' },
      ],
    }),

    // ============================================================
    // Update Integration
    // ============================================================
    updateIntegration: builder.mutation<IntegrationConnection, { id: string; data: UpdateIntegrationRequest }>({
      query: ({ id, data }) => ({
        url: `/integrations/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Integration', id },
        { type: 'Integration', id: 'LIST' },
        { type: 'Integration', id: 'STATUS' },
      ],
    }),

    // ============================================================
    // Delete Integration
    // ============================================================
    deleteIntegration: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/integrations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'Integration', id: 'LIST' },
        { type: 'Integration', id: 'STATUS' },
      ],
    }),

    // ============================================================
    // Test Integration Connection (by ID)
    // ============================================================
    // A successful test can flip status from `pending`/`error` to
    // `connected`, so it also needs to invalidate STATUS so the wizard
    // and the SettingsPage card both see the new state.
    testIntegration: builder.mutation<TestIntegrationResponse, string>({
      query: (id) => ({
        url: `/integrations/${id}/test`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Integration', id },
        { type: 'Integration', id: 'LIST' },
        { type: 'Integration', id: 'STATUS' },
      ],
    }),

    /**
     * Begin a provider OAuth authorization and get back the URL to send the
     * browser to.
     *
     * The browser used to navigate straight to
     * `/api/oauth/<provider>/start?workspaceId=..&token=<bearer>`, because the
     * start route needed authentication and a top-level navigation cannot
     * carry an Authorization header. That put a CRM access token in the
     * address bar, and therefore in browser history, in the `Referer` sent to
     * Google/Microsoft, and in every proxy and access log along the way.
     *
     * Asking over the normal authenticated API instead keeps the token in a
     * header. Only the provider's own consent URL — which carries no CRM
     * credential, just a single-use `state` — reaches the address bar.
     */
    startOAuth: builder.mutation<{ authUrl: string }, { provider: 'gmail' | 'outlook' | 'zoom' }>({
      query: ({ provider }) => ({
        url: `/oauth/${provider}/start`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  // Wizard endpoints
  useGetIntegrationStatusQuery,
  useTestIntegrationByProviderMutation,
  useSetupIntegrationMutation,
  // CRUD endpoints
  useListIntegrationsQuery,
  useGetIntegrationQuery,
  useCreateIntegrationMutation,
  useUpdateIntegrationMutation,
  useDeleteIntegrationMutation,
  useTestIntegrationMutation,
  // OAuth
  useStartOAuthMutation,
} = integrationsApi;
